import { Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type {
  AdTagCreative as AdTagCreativeType,
  DisplayCreative,
  FacebookDisplayCreative,
} from '@feathr/blackbox';
import { CreativeClass } from '@feathr/blackbox';
import { Button, Input, Toggle } from '@feathr/components';

import { CreativeDimensionsWell } from '../../../CreativeDimensionsWell';
import AdTagFields from './AdTagFields';
import MetaFields from './MetaFields';

import * as styles from '../CreativeForm.css';

interface ISetupTabProps {
  creative: DisplayCreative;
  /** Callback to open Bannersnack editor for builder-created creatives */
  onEditInBannersnack?: () => void;
}

function SetupTab({ creative, onEditInBannersnack }: Readonly<ISetupTabProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const isFacebook = creative.isFacebook;
  const isAdTag = creative.get('_cls') === CreativeClass.DisplayAdTag;
  const isBannersnack = creative.get('_cls') === CreativeClass.DisplayBannersnack;

  return (
    <Stack gap={theme.spacing['4']}>
      <CreativeDimensionsWell creative={creative} />

      <Input
        attribute={'name'}
        label={t('Creative name')}
        model={creative}
        placeholder={t('Enter name')}
        type={'text'}
      />

      {!isFacebook && isBannersnack && onEditInBannersnack && (
        <Button
          className={styles.fullWidth}
          name={'edit-in-bannersnack'}
          onClick={onEditInBannersnack}
          type={'secondary'}
        >
          {t('Edit in Bannersnack')}
        </Button>
      )}

      <Toggle
        label={t('Enable creative')}
        layout={'well'}
        onChange={(value) => creative.set({ is_enabled: !!value })}
        value={creative.get('is_enabled')}
      />

      {isAdTag && !isFacebook && (
        <AdTagFields creative={creative as unknown as AdTagCreativeType} />
      )}

      {isFacebook && <MetaFields creative={creative as FacebookDisplayCreative} />}
    </Stack>
  );
}

export default observer(SetupTab);
